type Language = "he" | "en";
let pendingStop: (() => void) | undefined;

export function cancelBrowserSpeech() {
  pendingStop?.();
  pendingStop = undefined;
  window.speechSynthesis?.cancel();
}

// Keep English phrases together, including punctuation inside words and phrases.
export function speechSegments(text: string, language: Language) {
  const pieces = text.split(/([A-Za-z]+(?:[’'-][A-Za-z]+)*(?:\s+[A-Za-z]+(?:[’'-][A-Za-z]+)*)*)/u);
  const segments: { text: string; language: Language }[] = [];
  for (const piece of pieces) {
    if (!piece) continue;
    if (!/[\p{L}\p{N}]/u.test(piece) && segments.length) {
      segments[segments.length - 1].text += piece;
    } else if (piece.trim()) {
      segments.push({ text: piece, language: /[A-Za-z]/.test(piece) ? "en" : language });
    }
  }
  return segments;
}

export function speakBrowser(lines: string[], language: Language, onEnd: (failed?: boolean) => void = () => {}) {
  cancelBrowserSpeech();
  const synth = window.speechSynthesis;
  let stopped = false;
  let timer: ReturnType<typeof setTimeout>;
  let utterance: SpeechSynthesisUtterance | undefined;
  const queue = lines.flatMap((line) => speechSegments(line, language).map((segment, index) => ({ ...segment, pause: index === 0 })));
  let index = 0;

  function stop() {
    stopped = true;
    clearTimeout(timer);
    synth.removeEventListener("voiceschanged", start);
    if (utterance) utterance.onend = utterance.onerror = null;
  }
  pendingStop = stop;
  function finish(failed = false) {
    if (stopped) return;
    cancelBrowserSpeech();
    onEnd(failed);
  }
  function next() {
    if (stopped) return;
    if (index === queue.length) return finish();
    const segment = queue[index++];
    const locale = segment.language === "he" ? "he-IL" : "en-US";
    try {
      utterance = new SpeechSynthesisUtterance(segment.text);
      const voices = synth.getVoices();
      const normalized = (lang: string) => lang.toLowerCase().replace(/_/g, "-").replace(/^iw(?=-|$)/, "he");
      const voice = voices.find((voice) => normalized(voice.lang) === locale.toLowerCase())
        || voices.find((voice) => normalized(voice.lang).split("-")[0] === segment.language);
      utterance.lang = locale;
      if (voice) utterance.voice = voice;
      utterance.rate = 0.9;
      utterance.onend = () => {
        if (stopped) return;
        if (queue[index]?.pause) timer = setTimeout(next, 250);
        else next();
      };
      utterance.onerror = () => finish(true);
      synth.speak(utterance);
    } catch { finish(true); }
  }
  function start() {
    if (stopped) return;
    clearTimeout(timer);
    synth.removeEventListener("voiceschanged", start);
    next();
  }
  // Wait briefly for asynchronous voice discovery, then let the browser resolve the locale.
  synth.addEventListener("voiceschanged", start);
  timer = setTimeout(start, synth.getVoices().length ? 0 : 500);
  return () => { if (!stopped) cancelBrowserSpeech(); };
}
